<template>
  <div class="arc-sec">
    <div class="arc-toolbar">
      <div class="spacer" />
      <button class="btn primary" type="button" @click="openNew">+ {{ $t('ARC_MGMT.INDICATORS.NEW') }}</button>
    </div>

    <div v-if="loading" class="muted">{{ $t('ARC_MGMT.LOADING') }}</div>
    <table v-else>
      <thead>
        <tr>
          <th>{{ $t('ARC_MGMT.INDICATORS.NAME') }}</th>
          <th>{{ $t('ARC_MGMT.PILLAR') }}</th>
          <th>{{ $t('ARC_MGMT.INDICATORS.BASE') }}</th>
          <th>{{ $t('ARC_MGMT.INDICATORS.NOW') }}</th>
          <th>{{ $t('ARC_MGMT.INDICATORS.GOAL') }}</th>
          <th>{{ $t('ARC_MGMT.INDICATORS.SOURCE') }}</th>
          <th>{{ $t('ARC_MGMT.INDICATORS.SITUATION') }}</th>
          <th />
        </tr>
      </thead>
      <tbody>
        <template v-for="k in items">
          <tr :key="k.id">
            <td class="nome">{{ k.nome }}</td>
            <td><span class="pl" :class="k.pilar">{{ k.pilar }}</span></td>
            <td>{{ fmt(k.base_valor, k) }}</td>
            <td><b>{{ fmt(k.atual, k) }}</b></td>
            <td>{{ fmt(k.meta_valor, k) }}</td>
            <td class="src">{{ sourceLabel(k) }}</td>
            <td><span class="sit" :class="k.situacao">{{ $t('ARC_MGMT.SITUATION.' + (k.situacao || 'sem_dados')) }}</span></td>
            <td class="acts">
              <button class="link" type="button" @click="toggle(k.id)">{{ expanded === k.id ? $t('ARC_MGMT.CLOSE') : $t('ARC_MGMT.INDICATORS.ENTRIES') }}</button>
              <button class="link" type="button" @click="openEdit(k)">{{ $t('ARC_MGMT.EDIT') }}</button>
            </td>
          </tr>
          <tr v-if="expanded === k.id" :key="k.id + '-exp'" class="exp">
            <td colspan="8">
              <div class="entries">
                <div v-for="mes in 12" :key="mes" class="cell">
                  <span>{{ $t('ARC_MGMT.INDICATORS.M') }}{{ mes }}</span>
                  <input v-model="draft[k.id][mes]" type="number" step="any" :placeholder="autoValor(k, mes)" :readonly="false" />
                </div>
              </div>
              <div class="entries-actions">
                <button class="btn primary" type="button" @click="saveValues(k)">{{ $t('ARC_MGMT.SAVE') }}</button>
                <button class="btn" type="button" @click="setBaseline(k)">{{ $t('ARC_MGMT.INDICATORS.SET_BASELINE') }}</button>
              </div>
            </td>
          </tr>
        </template>
      </tbody>
    </table>

    <div v-if="showModal" class="arc-modal-bg" @click.self="showModal = false">
      <div class="arc-modal">
        <h3>{{ editing.id ? $t('ARC_MGMT.EDIT') : $t('ARC_MGMT.INDICATORS.NEW') }}</h3>
        <label>{{ $t('ARC_MGMT.INDICATORS.NAME') }}<input v-model="editing.nome" type="text" /></label>
        <label>{{ $t('ARC_MGMT.INDICATORS.HOW') }}<textarea v-model="editing.descricao" rows="2" /></label>
        <div class="row">
          <label>{{ $t('ARC_MGMT.PILLAR') }}<select v-model="editing.pilar"><option>A</option><option>R</option><option>C</option></select></label>
          <label>{{ $t('ARC_MGMT.INDICATORS.UNIT') }}<input v-model="editing.unidade" type="text" placeholder="%" /></label>
          <label>{{ $t('ARC_MGMT.INDICATORS.DECIMALS') }}<input v-model.number="editing.casas_decimais" type="number" min="0" max="4" /></label>
        </div>
        <div class="row">
          <label>{{ $t('ARC_MGMT.INDICATORS.DIRECTION') }}
            <select v-model="editing.direcao"><option value="maior_melhor">Maior é melhor</option><option value="menor_melhor">Menor é melhor</option></select>
          </label>
          <label>{{ $t('ARC_MGMT.INDICATORS.SOURCE') }}
            <select v-model="editing.fonte"><option value="automatico">Automático</option><option value="integracao">Integração</option><option value="manual">Manual</option></select>
          </label>
          <label>{{ $t('ARC_MGMT.INDICATORS.WEIGHT') }}<input v-model.number="editing.peso" type="number" step="0.1" /></label>
        </div>
        <div class="row">
          <label>{{ $t('ARC_MGMT.INDICATORS.BASE') }}<input v-model.number="editing.base_valor" type="number" step="any" /></label>
          <label>{{ $t('ARC_MGMT.INDICATORS.GOAL') }}<input v-model.number="editing.meta_valor" type="number" step="any" /></label>
          <label class="chk"><input v-model="editing.cliente_pode_lancar" type="checkbox" /> {{ $t('ARC_MGMT.INDICATORS.CLIENT_CAN') }}</label>
        </div>
        <div class="modal-actions">
          <button class="btn danger-ghost" v-if="editing.id" type="button" @click="remove(editing)">{{ $t('ARC_MGMT.DELETE') }}</button>
          <div class="spacer" />
          <button class="btn" type="button" @click="showModal = false">{{ $t('ARC_MGMT.CANCEL') }}</button>
          <button class="btn primary" type="button" @click="saveKpi">{{ $t('ARC_MGMT.SAVE') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ArcAPI from 'dashboard/api/arc';

export default {
  props: { journey: { type: Object, default: () => ({}) }, cycle: { type: [Number, String], default: null } },
  data() {
    return { items: [], loading: true, expanded: null, draft: {}, showModal: false, editing: {} };
  },
  mounted() { this.load(); },
  methods: {
    async load() {
      this.loading = true;
      try {
        const { data } = await ArcAPI.kpis();
        this.items = data.payload || [];
      } finally {
        this.loading = false;
      }
    },
    fmt(v, k) {
      if (v === null || v === undefined) return '—';
      return Number(v).toLocaleString('pt-BR', { minimumFractionDigits: k.casas_decimais, maximumFractionDigits: k.casas_decimais }) + (k.unidade || '');
    },
    sourceLabel(k) {
      if (k.fonte === 'automatico') return 'Automático' + (k.metrica_automatica ? ` · ${k.metrica_automatica}` : '');
      if (k.fonte === 'integracao') return 'Integração' + (k.rotulo_fonte ? ` · ${k.rotulo_fonte}` : '');
      return 'Informado';
    },
    autoValor(k, mes) {
      const v = (k.valores || []).find(x => x.mes === mes);
      return v ? String(v.valor) : '';
    },
    toggle(id) {
      if (this.expanded === id) { this.expanded = null; return; }
      const k = this.items.find(x => x.id === id);
      const map = {};
      for (let m = 1; m <= 12; m += 1) {
        const v = (k.valores || []).find(x => x.mes === m);
        map[m] = v ? v.valor : '';
      }
      this.$set(this.draft, id, map);
      this.expanded = id;
    },
    async saveValues(k) {
      const values = [];
      for (let m = 1; m <= 12; m += 1) {
        const val = this.draft[k.id][m];
        if (val !== '' && val !== null && val !== undefined) values.push({ mes: m, valor: val });
      }
      await ArcAPI.saveKpiValues(k.id, values);
      this.expanded = null;
      this.load();
      this.$emit('refresh');
    },
    async setBaseline(k) {
      await ArcAPI.setKpiBaseline(k.id);
      this.load();
    },
    openNew() { this.editing = { pilar: 'A', unidade: '%', casas_decimais: 0, direcao: 'maior_melhor', fonte: 'manual', peso: 1, cliente_pode_lancar: false }; this.showModal = true; },
    openEdit(k) { this.editing = { ...k }; this.showModal = true; },
    async saveKpi() {
      const p = (({ nome, descricao, pilar, unidade, casas_decimais, direcao, fonte, metrica_automatica, rotulo_fonte, peso, cliente_pode_lancar, base_valor, meta_valor }) =>
        ({ nome, descricao, pilar, unidade, casas_decimais, direcao, fonte, metrica_automatica, rotulo_fonte, peso, cliente_pode_lancar, base_valor, meta_valor }))(this.editing);
      if (this.editing.id) await ArcAPI.updateKpi(this.editing.id, p);
      else await ArcAPI.createKpi(p);
      this.showModal = false;
      this.load();
      this.$emit('refresh');
    },
    async remove(k) {
      // eslint-disable-next-line no-alert
      if (!window.confirm(this.$t('ARC_MGMT.INDICATORS.DELETE_CONFIRM'))) return;
      await ArcAPI.deleteKpi(k.id);
      this.showModal = false;
      this.load();
    },
  },
};
</script>

<style lang="scss" scoped>
.arc-toolbar { display: flex; margin-bottom: var(--space-normal); }
.spacer { flex: 1; }
table { width: 100%; border-collapse: collapse; }
th { text-align: left; font-size: var(--font-size-mini); text-transform: uppercase; letter-spacing: .4px; color: var(--s-400); padding: var(--space-small); border-bottom: 1px solid var(--s-100); }
td { padding: var(--space-small); border-bottom: 1px solid var(--s-75); font-size: var(--font-size-small); color: var(--s-800); }
.nome { font-weight: var(--font-weight-medium); }
.src { color: var(--s-500); font-size: var(--font-size-mini); }
.pl { display: inline-grid; place-items: center; width: 1.8rem; height: 1.8rem; border-radius: var(--border-radius-normal); font-weight: var(--font-weight-bold); font-size: var(--font-size-mini);
  &.A { background: #E6F1FC; color: #1E88E5; } &.R { background: #E7F6EE; color: #1F9D63; } &.C { background: #EFECFF; color: #7B61FF; } }
.sit { font-size: var(--font-size-mini); font-weight: var(--font-weight-medium); padding: var(--space-micro) var(--space-small); border-radius: 9999px;
  &.no_ritmo { background: var(--g-100); color: var(--g-700); } &.atencao { background: var(--y-100); color: var(--y-700); }
  &.abaixo { background: var(--r-100); color: var(--r-600); } &.sem_dados { background: var(--s-75); color: var(--s-500); } }
.acts { text-align: right; white-space: nowrap; }
.link { border: 0; background: none; color: var(--w-600); font-size: var(--font-size-mini); cursor: pointer; padding: 0 var(--space-smaller); }
.exp td { background: var(--s-25); }
.entries { display: grid; grid-template-columns: repeat(6, 1fr); gap: var(--space-small); }
.cell { display: flex; flex-direction: column; gap: var(--space-micro); font-size: var(--font-size-mini); color: var(--s-500);
  input { border: 1px solid var(--s-200); border-radius: var(--border-radius-normal); padding: var(--space-micro) var(--space-smaller); background: var(--white); color: var(--s-800); } }
.entries-actions { display: flex; gap: var(--space-small); margin-top: var(--space-normal); }
.muted { color: var(--s-500); }
.btn { height: var(--space-large); padding: 0 var(--space-normal); border-radius: var(--border-radius-normal); border: 1px solid var(--s-200); background: var(--white); color: var(--s-700); font-weight: var(--font-weight-medium); cursor: pointer;
  &.primary { background: var(--w-500); border-color: var(--w-500); color: var(--white); } &.danger-ghost { border-color: var(--r-200); color: var(--r-500); } }
.arc-modal-bg { position: fixed; inset: 0; background: rgba(0,0,0,.4); display: grid; place-items: center; z-index: 9999; }
.arc-modal { background: var(--white); border-radius: var(--border-radius-large); padding: var(--space-large); width: 34rem; max-width: 92vw; max-height: 90vh; overflow: auto;
  h3 { margin: 0 0 var(--space-normal); color: var(--s-900); }
  label { display: flex; flex-direction: column; gap: var(--space-smaller); font-size: var(--font-size-small); color: var(--s-700); margin-bottom: var(--space-small); }
  input[type='text'], input[type='number'], textarea, select { border: 1px solid var(--s-200); border-radius: var(--border-radius-normal); padding: var(--space-smaller) var(--space-small); background: var(--white); color: var(--s-800); }
  .row { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: var(--space-small); }
  .chk { flex-direction: row; align-items: center; gap: var(--space-smaller); } }
.modal-actions { display: flex; align-items: center; gap: var(--space-small); margin-top: var(--space-normal); }
</style>
